/**
 * `detectRenderMode` — single classification entry point for VQE-RO.
 *
 * Maps a `(ParsedBuffer, cursor)` pair to the rendering surface VQE should
 * present. Uses `DocumentStructure.statementAt` + `StatementInfo.kind` only;
 * no regex (VQE-RO9). Procedural-body bodies always fall back to
 * `'unsupported-procedural'` in v1; future per-dialect resolvers will let
 * each procedural-grammar PRD light up structured rendering automatically.
 *
 * For SELECT, runs a paren-aware set-operator-precedence check; multi-level
 * trees downgrade to `'unsupported-set-precedence'` (rendered via the
 * Read-only Fallback). A SELECT whose FROM/JOIN target is a `GRAPH_TABLE(…)`
 * invocation (SQL/PGQ) downgrades to `'unsupported-graph-table'` — full VQE
 * would otherwise parse the GPML pattern as an editable `'function-call'` FROM
 * item (X1-T27; mirrors Task 26's compact `hasGraphTableFrom`). PRD:
 * `Ralph/VisualQueryEditorReadOnly/PRD.md` § Architecture → "Statement
 * detection + rendering mode".
 */
import type { ParseOptions, SqlRange, StatementKind } from '../../../sql-grammar/model';
import { skipContextFor, trySkipLeadingTrivia, trySkipNonCode } from '../../../sql-resolver/classify/tokenSkip';
import type { ParsedBuffer, SqlPosition } from '../../../sql-resolver/types';
import type { RenderMode } from '../types';

import { hasComplexSetOperatorPrecedence } from './detectSetOperatorPrecedence';

const DDL_KINDS: ReadonlySet<StatementKind> = new Set([
  'ddl-create',
  'ddl-alter',
  'ddl-drop',
  'ddl-truncate',
  'ddl-rename',
  'grant',
  'revoke',
]);

const GRAPH_TABLE_KEYWORD = 'GRAPH_TABLE';

function isWordBoundary(source: string, idx: number): boolean {
  if (idx < 0 || idx >= source.length) return true;
  return !/[A-Za-z0-9_]/.test(source[idx]);
}

function matchesGraphTableKeyword(source: string, i: number, end: number): boolean {
  if (i + GRAPH_TABLE_KEYWORD.length > end) return false;
  for (let k = 0; k < GRAPH_TABLE_KEYWORD.length; k++) {
    const ch = source[i + k];
    if (ch !== GRAPH_TABLE_KEYWORD[k] && ch !== GRAPH_TABLE_KEYWORD[k].toLowerCase()) return false;
  }
  return isWordBoundary(source, i - 1) && isWordBoundary(source, i + GRAPH_TABLE_KEYWORD.length);
}

/**
 * Masked-text detection of a `GRAPH_TABLE ( … )` invocation (SQL/PGQ) anywhere
 * in the statement's range. Structurally equivalent to Task 26's
 * `hasGraphTableFrom` (`compactDegradeDetail.ts`): a GRAPH_TABLE FROM/JOIN
 * target is an unqualified `GRAPH_TABLE` identifier immediately followed
 * (whitespace/comments aside) by `(`, so a plain relation named `graph_table`
 * (`FROM graph_table`, no parens) never matches — never a substring test. It
 * is also NEVER schema-qualified (mirrors T26's `isGraphTableTarget`, which
 * requires `!routine.schema`), so a preceding `.` — e.g.
 * `myschema.graph_table(...)`, an ordinary schema-qualified function/table
 * call — is rejected too (Fix round 1, ITEM 1).
 *
 * `detectRenderMode` operates on `(ParsedBuffer, cursor)` and carries no
 * `QueryStructure`, so this reuses the SAME masked character scan the sibling
 * `hasComplexSetOperatorPrecedence` uses: `trySkipNonCode` skips
 * strings/comments/quoted identifiers, so `SELECT 'GRAPH_TABLE(' …` and a
 * quoted `"GRAPH_TABLE"` relation name do not false-trigger, and the whole
 * range is scanned so an explicit `JOIN GRAPH_TABLE(…)` target is covered too.
 *
 * Scanning the WHOLE statement range (rather than only at FROM/JOIN clause
 * positions) is an intentional trade-off: it needs no clause-position
 * bookkeeping and stays a flat char-scan, at the cost of also (harmlessly)
 * matching an unqualified `GRAPH_TABLE(…)` written somewhere structurally odd
 * — accepted because real SQL never has a reason to write that token
 * elsewhere, and the schema-qualifier guard above is the one false-positive
 * shape that DOES occur in practice.
 */
function hasGraphTableInvocation(source: string, range: SqlRange, options: ParseOptions): boolean {
  const ctx = skipContextFor(options);
  const start = range.startOffset;
  const end = Math.min(range.endOffset, source.length);
  let i = start;
  // Nearest preceding non-whitespace, non-comment character seen so far in the
  // scan — used to reject a schema-qualified `x.graph_table(`. Left untouched
  // while skipping whitespace or a comment region (both trivia), so
  // `schema . graph_table(` and a comment sitting between the dot and the
  // keyword are rejected exactly like the no-space form.
  let prevChar = '';
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped > i ? skipped : i + 1;
      continue;
    }
    if ((source[i] === 'G' || source[i] === 'g') && matchesGraphTableKeyword(source, i, end)) {
      if (prevChar === '.') {
        // Schema-qualified — never a real GRAPH_TABLE invocation. Skip past
        // it and keep scanning the rest of the statement.
        i += GRAPH_TABLE_KEYWORD.length;
        prevChar = source[i - 1] ?? prevChar;
        continue;
      }
      let j = i + GRAPH_TABLE_KEYWORD.length;
      // Skip whitespace + comment trivia between the keyword and a possible `(`.
      while (j < end) {
        if (/\s/.test(source[j])) {
          j++;
          continue;
        }
        const trivia = trySkipLeadingTrivia(source, j, end, ctx);
        if (trivia > j) {
          j = trivia;
          continue;
        }
        break;
      }
      if (j < end && source[j] === '(') return true;
      i += GRAPH_TABLE_KEYWORD.length;
      prevChar = source[i - 1] ?? prevChar;
      continue;
    }
    if (!/\s/.test(source[i])) prevChar = source[i];
    i++;
  }
  return false;
}

function hasErrorOverlap(buffer: ParsedBuffer, statementId: string): boolean {
  const stmt = buffer.documentStructure.statements.find((s) => s.id === statementId);
  if (!stmt) return false;
  for (const range of buffer.parsedDocument.recovery.errorRanges) {
    if (range.endOffset < stmt.range.startOffset) continue;
    if (range.startOffset > stmt.range.endOffset) continue;
    return true;
  }
  return false;
}

export function detectRenderMode(buffer: ParsedBuffer, cursor: SqlPosition): RenderMode {
  const stmt = buffer.documentStructure.statementAt(cursor.offset);
  if (!stmt) return 'parse-error';

  switch (stmt.kind) {
    case 'select': {
      // GRAPH_TABLE (SQL/PGQ) has no editable structured surface — check it
      // FIRST so the fallback shows the accurate graph-query message rather
      // than a set-precedence one for a graph query that happens to also nest
      // parentheses (X1-T27).
      if (
        hasGraphTableInvocation(
          buffer.parsedDocument.source,
          stmt.range,
          buffer.parsedDocument.options,
        )
      ) {
        return 'unsupported-graph-table';
      }
      const complex = hasComplexSetOperatorPrecedence(
        buffer.parsedDocument.source,
        stmt.range,
        buffer.parsedDocument.options,
      );
      return complex ? 'unsupported-set-precedence' : 'select';
    }
    case 'insert':
    case 'update':
    case 'delete':
    case 'merge':
      return stmt.kind;
    case 'procedural-body':
      return 'unsupported-procedural';
    case 'unknown':
      return hasErrorOverlap(buffer, stmt.id) ? 'parse-error' : 'unsupported-unknown';
    default:
      if (DDL_KINDS.has(stmt.kind)) return 'unsupported-ddl';
      return 'unsupported-ddl';
  }
}
